"use client"

import Link from "next/link"
import { ExternalLink, Package, ArrowRight } from "lucide-react"
import { contentAnalytics } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"

// Shared surface for the impression/click pair so the guide sidebar's CTR is
// computable. The legacy affiliate_link_clicked call keeps its `sidebar`
// placement so historical queries stay intact.
const SIDEBAR_SURFACE = "guide-sidebar"

export interface SidebarProduct {
  name: string
  description: string
  affiliateUrl: string
  price: string
  imageUrl?: string
}

interface GuideSidebarProductProps {
  slug: string
  products: SidebarProduct[]
  sourcePrefix?: string
  /** Experiment arm label stamped on the rail impression as `guide_rail_variant`
   *  so the control arm of guide_affiliate_rail_v1 is separable in SQL. Omitted
   *  everywhere the rail is not under test (blog rails), which leaves the event
   *  payload byte-identical to today. */
  railVariant?: string
}

export function GuideSidebarProduct({ slug, products, sourcePrefix = "guide-sidebar", railVariant }: GuideSidebarProductProps) {
  // Hook stays above the early return so the call order is unconditional. With
  // no picks the ref never attaches, so no impression fires.
  const impressionRef = useAffiliateImpression(SIDEBAR_SURFACE, {
    article_slug: slug,
    placement: 'sidebar',
    product_count: products.length,
    // One impression per RAIL (whole card set), not per product.
    denominator: 'container',
    ...(railVariant ? { guide_rail_variant: railVariant } : {}),
  })

  if (products.length === 0) return null

  const handleClick = (product: SidebarProduct) => {
    contentAnalytics.affiliateLinkClicked(
      slug,
      product.name,
      product.affiliateUrl,
      'sidebar'
    )
    // Unified click on the SAME surface as the impression above.
    contentAnalytics.affiliateProductClicked(SIDEBAR_SURFACE, {
      productName: product.name,
      affiliateLink: product.affiliateUrl,
      article_slug: slug,
      placement: 'sidebar',
    })
  }

  return (
    <div ref={impressionRef} className="ds-glass rounded-[18px] p-4" style={{ boxShadow: "0 4px 16px -8px rgba(18,46,30,0.22), 0 1px 3px -1px rgba(18,46,30,0.12)" }}>
      <div className="flex items-center gap-2 mb-3">
        <span className="grid place-items-center w-6 h-6 rounded-lg border border-white/70 bg-green-50/85 text-green-700 flex-shrink-0">
          <Package className="w-3.5 h-3.5" />
        </span>
        <h3 className="text-[11px] font-bold text-green-700 uppercase tracking-[0.08em]">
          Top Picks
        </h3>
      </div>

      <div className="space-y-2">
        {products.map((product) => (
          <a
            key={product.name}
            href={product.affiliateUrl}
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
            data-affiliate="true"
            onClick={() => handleClick(product)}
            className="group flex items-start gap-3 rounded-2xl border border-white/55 bg-white/55 backdrop-blur-md p-2.5 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_-14px_rgba(22,163,74,0.4)]"
          >
            {/* Product thumbnail — plain img (ready-to-use Amazon CDN URL, no
                next/image domain config needed). Falls back to a Package glyph
                when the pick has no image. */}
            <span className="grid h-12 w-12 flex-shrink-0 place-items-center overflow-hidden rounded-lg border border-white/70 bg-white">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-contain p-1"
                />
              ) : (
                <Package className="w-5 h-5 text-green-600/70" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-1.5">
                <span className="text-[13px] font-semibold text-gray-900 group-hover:text-green-700 leading-snug flex-1">
                  {product.name}
                </span>
                <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-green-600" />
              </div>
              <span className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-semibold text-green-700 group-hover:text-green-800">
                Check price on Amazon
                <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
          </a>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-white/45">
        <Link
          href={`/products?source=${encodeURIComponent(`${sourcePrefix}-${slug}`)}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:text-green-800 group"
        >
          See all products
          <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  )
}
